import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Fade,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Input,
  Skeleton,
  SlideFade,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Variants, motion } from "framer-motion";
import { HiChevronRight, HiXMark } from "react-icons/hi2";

import React from "react";
import { Link } from "react-router-dom";
import { images } from "../assets/images/images";
import { useResetPassword } from "../hooks/useResetPassword";

const children: Variants = {
  hidden: { y: 100, opacity: 0, transition: { duration: 0.8 } },
  show: { y: 0, opacity: 1, transition: { duration: 0.8 } },
};

const ResetPassword: React.FC = () => {
  const {
    token,
    mode,
    email,
    setEmail,
    password,
    setPassword,
    password2,
    setPassword2,
    emailSent,
    loading,
    valid,
    errorMsgs,
    requestResetPassword,
    handlePwdReset,
  } = useResetPassword();

  return (
    <Flex
      w={"full"}
      minH={"100vh"}
      alignItems={"center"}
      justifyContent={"center"}
      m={"0 auto"}
      bgColor={"gray.100"}
      bgImage={images.pattern_primary}
      bgSize={"cover"}
    >
      <SlideFade
        in={true}
        style={{ width: "100%" }}
        offsetY={"50px"}
        transition={{ enter: { duration: 0.5 } }}
      >
        <VStack
          spacing={8}
          w={"full"}
          maxW={"lg"}
          alignItems={"flex-start"}
          justifyContent={"center"}
          m={"0 auto"}
          bgColor={"white"}
          p={{ base: 6, md: 12 }}
          borderRadius={"xl"}
        >
          {" "}
          {/* AUTH BODY */}
          <VStack
            spacing={8}
            w={"full"}
            h={"full"}
            justifyContent={"center"}
            alignItems={"center"}
            maxW={"lg"}
            as={motion.div}
            variants={children}
          >
            <VStack
              spacing={4}
              justifyContent={"center"}
              alignItems={"center"}
              w={"full"}
              textAlign={"center"}
            >
              <Heading as="h1" fontWeight={"extrabold"} size="xl">
                Recupera tu contraseña
              </Heading>
              <Text size={"md"}>
                {mode === "request"
                  ? "Introduce tu correo electrónico para recibir un enlace de restablecimiento de contraseña."
                  : "Introduce tu nueva contraseña."}
              </Text>
            </VStack>

            {/* PASSWORD RESET REQUEST FORM */}
            <VStack
              spacing={8}
              as={"form"}
              w={"full"}
              alignItems={"center"}
              onSubmit={(e: React.FormEvent<HTMLDivElement>) => {
                e.preventDefault();
                if (mode === "request") {
                  requestResetPassword(email);
                } else {
                  handlePwdReset(token!, password);
                }
              }}
            >
              {!mode ? (
                <>
                  <Skeleton height={10} width={200} />
                </>
              ) : mode === "request" ? (
                <PasswordResetRequestForm
                  email={email}
                  setEmail={setEmail}
                  emailSent={emailSent}
                />
              ) : (
                <PasswordResetForm
                  password={password}
                  setPassword={setPassword}
                  password2={password2}
                  setPassword2={setPassword2}
                />
              )}

              {!emailSent && (
                <Button
                  w={"full"}
                  justifyContent={"space-between"}
                  size={"lg"}
                  colorScheme="brand"
                  type="submit"
                  rightIcon={<Icon as={HiChevronRight} />}
                  isLoading={loading}
                  isDisabled={!valid || (mode === "change" && !token)}
                >
                  {mode === "request" ? "Recibir enlace" : "Cambiar contraseña"}
                </Button>
              )}

              <Fade in={errorMsgs.length > 0}>
                {errorMsgs &&
                  errorMsgs.map((msg, i) => (
                    <HStack key={i} alignItems={"start"}>
                      <Icon
                        as={HiXMark}
                        color={"red.500"}
                        strokeWidth={1}
                        h={5}
                        w={5}
                        mt={"0.5"}
                      />
                      <Text color={"red.500"}>{msg}</Text>
                    </HStack>
                  ))}
              </Fade>

              <Text fontWeight={"medium"} fontSize={14} color={"gray.500"}>
                ¿Ya tienes tu contraseña?{" "}
                <Link to={"/auth"}>
                  <Box
                    as={"span"}
                    fontWeight={"medium"}
                    color={"gray.800"}
                    textDecor={"underline"}
                  >
                    Inicia sesión
                  </Box>
                </Link>
              </Text>
            </VStack>
          </VStack>
        </VStack>
      </SlideFade>
    </Flex>
  );
};

export default ResetPassword;

const PasswordResetRequestForm: React.FC<{
  email: string;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  emailSent: boolean;
}> = ({ email, setEmail, emailSent }) => {
  return (
    <VStack w={"full"} alignItems={"center"} spacing={3}>
      {!emailSent ? (
        <FormControl id="email" isRequired>
          <FormLabel fontWeight={"medium"}>Correo electrónico</FormLabel>
          <Input
            type="email"
            size={"lg"}
            value={email}
            placeholder={"mail@dominio.com"}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </FormControl>
      ) : (
        <Alert
          status="success"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            Consulta tu correo electrónico
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            Te hemos enviado un correo electrónico con un enlace para
            restablecer tu contraseña.
          </AlertDescription>
        </Alert>
      )}
    </VStack>
  );
};

const PasswordResetForm: React.FC<{
  password: string;
  setPassword: React.Dispatch<React.SetStateAction<string>>;
  password2: string;
  setPassword2: React.Dispatch<React.SetStateAction<string>>;
}> = ({ password, setPassword, password2, setPassword2 }) => {
  return (
    <VStack w={"full"} alignItems={"center"} spacing={3}>
      <FormControl id="password" isRequired>
        <FormLabel fontWeight={"medium"}>Nueva contraseña</FormLabel>
        <Input
          type="password"
          size={"lg"}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
        />
      </FormControl>
      <FormControl id="password2" isRequired>
        <FormLabel fontWeight={"medium"}>Repite la contraseña</FormLabel>
        <Input
          type="password"
          size={"lg"}
          value={password2}
          onChange={(e) => {
            setPassword2(e.target.value);
          }}
        />
      </FormControl>
    </VStack>
  );
};
